import { createHouse as apiCreateHouse, ApiError } from './apiService';

/**
 * Cola local + envío para el registro de vivienda (`POST /v1/houses`).
 * Sigue el mismo patrón offline-first que `captureService.js`
 * (encuestas dinámicas por `id`), pero con su propia cola en
 * localStorage ya que el payload y el endpoint son distintos.
 *
 * Geolocalización deshabilitada por ahora: `location.latitude`,
 * `location.longitude` y `location.accuracy` siempre se envían en 0
 * (ver nota equivalente en `captureService.js`).
 */

const QUEUE_KEY = 'pulso17.houses.queue';
export const MAX_QUEUED_HOUSES = 10;

/** Error específico para cuando la cola local ya llegó al máximo permitido. */
export class HouseQueueFullError extends Error {
  constructor() {
    super(
      `Ya tienes ${MAX_QUEUED_HOUSES} viviendas guardadas sin sincronizar. Sincroniza antes de capturar otra.`
    );
    this.name = 'HouseQueueFullError';
  }
}

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Fecha-hora ISO 8601 con offset local, como la espera el backend. */
function formatIsoWithOffset(date) {
  const pad = (n) => String(n).padStart(2, '0');
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offset}`
  );
}

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/** Registros de vivienda guardados localmente, pendientes de sincronizar. */
export function getQueuedHouses() {
  return readQueue();
}

/** Cantidad de registros pendientes (para badges/KPIs). */
export function getQueuedHousesCount() {
  return readQueue().length;
}

/**
 * Arma el payload de `POST /v1/houses` a partir de las respuestas del
 * formulario dinámico `house-registration`. `answers` debe ser un
 * mapa `questionKey -> valor` (ya resuelto por el llamador, ya que las
 * respuestas del formulario vienen indexadas por `question.id`).
 *
 * @param {{ answersByKey: Record<string, any>, deviceId?: string }} params
 */
export function buildHousePayload({ answersByKey, deviceId = 'device-001' }) {
  const now = new Date();
  const capturedAt = formatIsoWithOffset(now);

  return {
    local_uuid: generateUuid(),
    device_id: deviceId,
    captured_at: capturedAt,
    synced_at: null,
    location: {
      latitude: 0,
      longitude: 0,
      accuracy: 0,
      captured_at: capturedAt,
    },
    answers: answersByKey,
  };
}

/**
 * Guarda un registro de vivienda en la cola local. Lanza
 * `HouseQueueFullError` si ya se alcanzó `MAX_QUEUED_HOUSES`.
 *
 * @param {Object} house ver `buildHousePayload`.
 */
export function enqueueHouse(house) {
  const queue = readQueue();
  if (queue.length >= MAX_QUEUED_HOUSES) {
    throw new HouseQueueFullError();
  }
  writeQueue([...queue, house]);
  return house;
}

/**
 * Sincroniza todos los registros de vivienda pendientes contra
 * `POST /v1/houses` (uno por uno). Los que se envían con éxito se
 * quitan de la cola; si alguno falla, se detiene y la cola conserva
 * los pendientes (incluido el que falló) para reintentar después.
 *
 * @returns {Promise<{ synced: number }>}
 */
export async function syncQueuedHouses() {
  const queue = readQueue();
  let synced = 0;
  const remaining = [...queue];

  while (remaining.length > 0) {
    const house = remaining[0];
    try {
      await apiCreateHouse({ ...house, synced_at: formatIsoWithOffset(new Date()) });
      remaining.shift();
      synced += 1;
      writeQueue(remaining);
    } catch (err) {
      writeQueue(remaining);
      throw err;
    }
  }

  return { synced };
}

/** `true` si el error viene de que el backend no está disponible. */
export function isNetworkError(err) {
  return err instanceof ApiError && err.status === 0;
}
